<script setup lang="ts">
/**
 * @fileoverview Provider component for tenant-specific theming
 * Applies tenant branding to Vuetify theme and CSS custom properties.
 *
 * Falls back to centralized defaults from brandingDefaults.ts
 * @module components/tenant/TenantThemeProvider
 * @see src/config/brandingDefaults.ts for color source of truth
 */
import { watch, onMounted, computed, onUnmounted } from 'vue'
import { useTheme } from 'vuetify'
import { useTenantStore } from '@/stores/tenant'
import type { TenantBranding } from '@/types'
import { COLOR_DEFAULTS, FONT_DEFAULTS } from '@/config/brandingDefaults'

const tenantStore = useTenantStore()
const vuetifyTheme = useTheme()

const branding = computed(() => tenantStore.branding)

/**
 * Default brand colors from centralized config
 * @see src/config/brandingDefaults.ts
 */
const defaultColors = {
  primary: COLOR_DEFAULTS.primary,
  secondary: COLOR_DEFAULTS.secondary,
  accent: COLOR_DEFAULTS.accent,
  info: COLOR_DEFAULTS.info,
  error: COLOR_DEFAULTS.error,
  warning: COLOR_DEFAULTS.warning,
  success: COLOR_DEFAULTS.success,
  background: COLOR_DEFAULTS.background,
  surface: COLOR_DEFAULTS.surface,
}

/**
 * Apply tenant branding to Vuetify theme and CSS custom properties
 */
function applyTheme(brandingData: TenantBranding | null) {
  const colors = brandingData?.colors ?? defaultColors
  const fonts = brandingData?.fonts

  // Update Vuetify theme colors
  vuetifyTheme.themes.value.light.colors = {
    ...vuetifyTheme.themes.value.light.colors,
    primary: colors.primary || defaultColors.primary,
    secondary: colors.secondary || defaultColors.secondary,
    error: colors.error || defaultColors.error,
    warning: colors.warning || defaultColors.warning,
    success: colors.success || defaultColors.success,
    info: colors.info || defaultColors.info,
    background: colors.background || defaultColors.background,
    surface: colors.surface || defaultColors.surface
  }

  // Apply CSS custom properties for non-Vuetify components
  const root = document.documentElement
  Object.entries(colors).forEach(([key, value]) => {
    if (value) {
      root.style.setProperty(`--color-${kebabCase(key)}`, value)
    }
  })

  // Load custom fonts if specified
  if (fonts?.heading || fonts?.body) {
    loadGoogleFonts(fonts)
  }

  // Update document head elements
  if (brandingData) {
    updateDocumentHead(brandingData)
  }

  // Apply custom CSS if allowed
  if (brandingData?.customCss) {
    applyCustomCss(brandingData.customCss)
  }
}

/**
 * Dynamically load Google Fonts
 */
function loadGoogleFonts(fonts: { heading?: string; body?: string }) {
  // Remove existing tenant fonts
  const existingLink = document.getElementById('tenant-fonts')
  if (existingLink) existingLink.remove()

  const families = [...new Set([fonts.heading, fonts.body].filter(Boolean))] as string[]
  if (families.length === 0) return

  const link = document.createElement('link')
  link.id = 'tenant-fonts'
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?${families
    .map(f => `family=${f.replace(/\s+/g, '+')}:wght@400;500;600;700`)
    .join('&')}&display=swap`
  document.head.appendChild(link)

  // Apply fonts to CSS custom properties with centralized fallback
  const root = document.documentElement
  const fontFallback = FONT_DEFAULTS.fallback
  if (fonts.heading) {
    root.style.setProperty('--font-heading', `"${fonts.heading}", ${fontFallback}`)
  }
  if (fonts.body) {
    root.style.setProperty('--font-body', `"${fonts.body}", ${fontFallback}`)
  }
}

/**
 * Update favicon and document title
 */
function updateDocumentHead(brandingData: TenantBranding) {
  // Update favicon
  if (brandingData.logo?.favicon) {
    let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null
    if (!favicon) {
      favicon = document.createElement('link')
      favicon.rel = 'icon'
      document.head.appendChild(favicon)
    }
    favicon.href = brandingData.logo.favicon
  }

  // Update Apple touch icon
  if (brandingData.logo?.appleTouchIcon) {
    let touchIcon = document.querySelector(
      'link[rel="apple-touch-icon"]'
    ) as HTMLLinkElement | null
    if (!touchIcon) {
      touchIcon = document.createElement('link')
      touchIcon.rel = 'apple-touch-icon'
      document.head.appendChild(touchIcon)
    }
    touchIcon.href = brandingData.logo.appleTouchIcon
  }

  // Update theme color meta tag
  if (brandingData.colors?.primary) {
    let themeColor = document.querySelector(
      'meta[name="theme-color"]'
    ) as HTMLMetaElement | null
    if (!themeColor) {
      themeColor = document.createElement('meta')
      themeColor.name = 'theme-color'
      document.head.appendChild(themeColor)
    }
    themeColor.content = brandingData.colors.primary
  }
}

/**
 * Apply sanitized custom CSS
 */
function applyCustomCss(css: string) {
  // Remove existing custom CSS
  const existingStyle = document.getElementById('tenant-custom-css')
  if (existingStyle) existingStyle.remove()

  // Basic sanitization - remove potentially dangerous rules
  const sanitized = css
    .replace(/@import/gi, '/* @import blocked */')
    .replace(/expression\(/gi, '/* expression blocked */')
    .replace(/javascript:/gi, '/* javascript blocked */')
    .replace(/behavior:/gi, '/* behavior blocked */')

  const style = document.createElement('style')
  style.id = 'tenant-custom-css'
  style.textContent = sanitized
  document.head.appendChild(style)
}

/**
 * Convert camelCase to kebab-case
 */
function kebabCase(str: string): string {
  return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
}

/**
 * Reset theme to defaults
 */
function resetTheme() {
  const root = document.documentElement

  // Remove custom CSS
  const customCss = document.getElementById('tenant-custom-css')
  if (customCss) customCss.remove()

  // Remove custom fonts
  const fontLink = document.getElementById('tenant-fonts')
  if (fontLink) fontLink.remove()

  // Reset CSS custom properties
  Object.keys(defaultColors).forEach(key => {
    root.style.removeProperty(`--color-${kebabCase(key)}`)
  })
  root.style.removeProperty('--font-heading')
  root.style.removeProperty('--font-body')
}

// Watch for branding changes and apply theme
watch(branding, (newBranding) => {
  applyTheme(newBranding)
}, { immediate: true, deep: true })

onMounted(() => {
  if (branding.value) {
    applyTheme(branding.value)
  }
})

onUnmounted(() => {
  resetTheme()
})
</script>

<template>
  <slot />
</template>
